import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '../lib/api';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

const ProductList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products', i18n.language],
    queryFn: () => productsApi.getAll(i18n.language).then((res: any) => res.data.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => productsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success(t('productDeleted'));
    },
    onError: () => {
      toast.error(t('errorOccurred'));
    },
  });

  const filteredProducts = products.filter((product: any) =>
    product.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">
          {i18n.language === 'ar' ? 'إدارة المنتجات' : 'Products Management'}
        </h1>
        <Button onClick={() => navigate('/products/new')}>
          <Plus className="h-4 w-4 mr-2" />
          {i18n.language === 'ar' ? 'إضافة منتج' : 'Add Product'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{i18n.language === 'ar' ? 'قائمة المنتجات' : 'Products List'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder={i18n.language === 'ar' ? 'البحث في المنتجات...' : 'Search products...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>

          <div className={`overflow-x-auto ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
            <Table className={i18n.language === 'ar' ? '[&_th]:text-right [&_td]:text-right' : '[&_th]:text-left [&_td]:text-left'}>
              <TableHeader>
                <TableRow>
                  <TableHead>{i18n.language === 'ar' ? 'العنوان' : 'Title'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'الوصف' : 'Description'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'المزايا' : 'Features'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'حالات الاستخدام' : 'Use Cases'}</TableHead>
                  <TableHead>{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product: any) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <div className="font-medium">
                        {product.title || (i18n.language === 'ar' ? 'بدون عنوان' : 'No title')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {product.description || (i18n.language === 'ar' ? 'بدون وصف' : 'No description')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {product.features && product.features.length > 0 ? (
                          product.features.slice(0, 2).map((feature: any, index: number) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {typeof feature === 'string' ? feature : feature.value}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">
                            {i18n.language === 'ar' ? 'لا توجد مزايا' : 'No features'}
                          </span>
                        )}
                        {product.features && product.features.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{product.features.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {product.use_cases && product.use_cases.length > 0 ? (
                          product.use_cases.slice(0, 2).map((useCase: any, index: number) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {typeof useCase === 'string' ? useCase : useCase.value}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">
                            {i18n.language === 'ar' ? 'لا توجد حالات استخدام' : 'No use cases'}
                          </span>
                        )}
                        {product.use_cases && product.use_cases.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{product.use_cases.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2 rtl:space-x-reverse">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/products/${product.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/products/${product.id}/edit`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {i18n.language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {i18n.language === 'ar' 
                                  ? 'هل أنت متأكد من حذف هذا المنتج؟ لا يمكن التراجع عن هذا الإجراء.'
                                  : 'Are you sure you want to delete this product? This action cannot be undone.'
                                }
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>
                                {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(product.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                {i18n.language === 'ar' ? 'حذف' : 'Delete'}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {i18n.language === 'ar' ? 'لا توجد منتجات' : 'No products found'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductList;
